/**
 * Chaupar Game Engine
 *
 * Core game logic: board initialization, dice rolling (3 pāsā with values [1,2,5,6]),
 * pawn movement, capture detection (permanent elimination), blocking (same-team pawns),
 * special combinations (triples), home column logic, team win condition,
 * turn management, and serialization.
 *
 * Key differences from PachisiEngine:
 * - 3 dice with values [1, 2, 5, 6] (historically authentic pāsā)
 * - NO safe squares — captures anywhere
 * - Captures ELIMINATE permanently (not back to start)
 * - Team-based: Red+Green vs Blue+Yellow
 * - Blocking: 2 same-TEAM pawns form impassable block
 * - Special triples grant extra turns + bonus moves
 * - Pawns start on the track at authentic positions (no entry mechanic)
 */

import {
  PlayerColor,
  TeamColor,
  PlayerType,
  PawnLocation,
  PawnPosition,
  PawnInfo,
  ChauparMove,
  TurnMoveSet,
  MoveValidation,
  MoveResult,
  TurnResult,
  MoveRecord,
  DiceResult,
  ChauparGameState,
  ChauparGameStatus,
  ChauparGameSettings,
  PLAYER_COLORS,
  PLAYER_START_POSITIONS,
  HOME_COLUMN_ENTRY,
  TRACK_LENGTH,
  HOME_COLUMN_LENGTH,
  PAWNS_PER_PLAYER,
  NUM_DICE,
  TEAMS,
  TRIPLE_BONUS,
  PLAYERS_PER_TEAM,
  DICE_VALUES,
  PAWN_START_OFFSETS,
} from './types';

export class ChauparEngine {
  private state: ChauparGameState;

  constructor() {
    this.state = this.createEmptyState();
  }

  // ============================================================================
  // Initialization
  // ============================================================================

  private createEmptyState(): ChauparGameState {
    const pawns: Record<PlayerColor, PawnPosition[]> = {
      red: [], blue: [], green: [], yellow: [],
    };
    for (const color of PLAYER_COLORS) {
      const startPos = PLAYER_START_POSITIONS[color];
      pawns[color] = PAWN_START_OFFSETS.map((offset, i) => ({
        pawnIndex: i,
        location: 'track' as PawnLocation,
        trackPosition: (startPos + offset) % TRACK_LENGTH,
      }));
    }

    return {
      pawns,
      eliminatedPawns: { red: 0, blue: 0, green: 0, yellow: 0 },
      activePlayers: [...PLAYER_COLORS],
      playerTypes: { red: 'human', blue: 'ai', green: 'ai', yellow: 'ai' },
      teams: { ...TEAMS },
      currentTurn: 'red',
      lastDiceResult: null,
      usedDice: [false, false, false],
      awaitingMove: false,
      pawnsHome: { red: 0, blue: 0, green: 0, yellow: 0 },
      moveHistory: [],
      gameStatus: { type: 'active' },
      gameId: this.generateId(),
      startedAt: Date.now(),
      lastMoveAt: Date.now(),
    };
  }

  initializeGame(playerTypes: PlayerType[], settings?: Partial<ChauparGameSettings>): void {
    this.state = this.createEmptyState();

    // Assign player types
    PLAYER_COLORS.forEach((color, index) => {
      this.state.playerTypes[color] = playerTypes[index] || 'ai';
    });

    this.state.currentTurn = 'red';
    this.state.gameStatus = { type: 'active' };
  }

  // ============================================================================
  // Dice Rolling
  // ============================================================================

  rollDice(): DiceResult {
    const values: [number, number, number] = [
      DICE_VALUES[Math.floor(Math.random() * DICE_VALUES.length)],
      DICE_VALUES[Math.floor(Math.random() * DICE_VALUES.length)],
      DICE_VALUES[Math.floor(Math.random() * DICE_VALUES.length)],
    ];
    return this.setDiceResult(values);
  }

  setDiceResult(values: [number, number, number]): DiceResult {
    const isTriple = values[0] === values[1] && values[1] === values[2];
    let bonusValue = 0;
    if (isTriple && TRIPLE_BONUS[values[0]] !== undefined) {
      bonusValue = TRIPLE_BONUS[values[0]];
    }

    const result: DiceResult = {
      values,
      isTriple,
      bonusValue,
      player: this.state.currentTurn,
    };

    this.state.lastDiceResult = result;
    this.state.usedDice = [false, false, false];
    this.state.awaitingMove = true;

    return result;
  }

  // ============================================================================
  // Move Validation & Execution
  // ============================================================================

  getValidMoves(player: PlayerColor): ChauparMove[] {
    if (this.state.gameStatus.type !== 'active') return [];
    if (this.state.currentTurn !== player) return [];
    if (!this.state.lastDiceResult) return [];

    const moves: ChauparMove[] = [];
    const diceResult = this.state.lastDiceResult;

    for (let dieIndex = 0; dieIndex < NUM_DICE; dieIndex++) {
      if (this.state.usedDice[dieIndex]) continue;

      const dieValue = diceResult.values[dieIndex];

      for (let pawnIndex = 0; pawnIndex < PAWNS_PER_PLAYER; pawnIndex++) {
        const pawn = this.state.pawns[player][pawnIndex];

        if (pawn.location === 'eliminated' || pawn.location === 'home') continue;

        // Movement on track
        if (pawn.location === 'track') {
          const currentPos = pawn.trackPosition!;
          const distanceTraveled = this.getDistanceTraveled(player, currentPos);
          const totalAfterMove = distanceTraveled + dieValue;

          if (totalAfterMove <= TRACK_LENGTH) {
            // Still on track
            const newPos = (currentPos + dieValue) % TRACK_LENGTH;
            if (!this.isPathBlockedForMove(player, currentPos, dieValue, 'track')) {
              moves.push({
                player,
                pawnIndex,
                dieIndex,
                dieValue,
                action: 'move',
              });
            }
          } else if (totalAfterMove <= TRACK_LENGTH + HOME_COLUMN_LENGTH) {
            // Entering or moving in home column
            if (!this.isPathBlockedForMove(player, currentPos, dieValue, 'track')) {
              moves.push({
                player,
                pawnIndex,
                dieIndex,
                dieValue,
                action: 'move',
              });
            }
          }
          // If totalAfterMove > TRACK_LENGTH + HOME_COLUMN_LENGTH, overshoot — not valid
          continue;
        }

        // Movement in home column
        if (pawn.location === 'home_column') {
          const currentHomePos = pawn.homeColumnPosition!;
          const newHomePos = currentHomePos + dieValue;
          if (newHomePos === HOME_COLUMN_LENGTH) {
            // Exact roll to reach home (Charkoni)
            moves.push({
              player,
              pawnIndex,
              dieIndex,
              dieValue,
              action: 'move',
            });
          } else if (newHomePos < HOME_COLUMN_LENGTH) {
            moves.push({
              player,
              pawnIndex,
              dieIndex,
              dieValue,
              action: 'move',
            });
          }
          // Overshoot — not valid
          continue;
        }
      }

      // Skip option: if no pawn can use this die, allow skip
      moves.push({
        player,
        pawnIndex: -1,
        dieIndex,
        dieValue,
        action: 'skip',
      });
    }

    // Also add bonus move if applicable
    if (diceResult.bonusValue > 0 && !this.state.usedDice.includes(true)) {
      // Bonus moves are handled after all 3 dice are used
    }

    return moves;
  }

  isValidMove(player: PlayerColor, move: ChauparMove): MoveValidation {
    if (this.state.gameStatus.type !== 'active') {
      return { valid: false, reason: 'game_over' };
    }
    if (this.state.currentTurn !== player) {
      return { valid: false, reason: 'not_your_turn' };
    }
    if (!this.state.lastDiceResult) {
      return { valid: false, reason: 'not_your_turn' };
    }
    if (move.dieIndex < 0 || move.dieIndex >= NUM_DICE) {
      return { valid: false, reason: 'die_already_used' };
    }
    if (this.state.usedDice[move.dieIndex]) {
      return { valid: false, reason: 'die_already_used' };
    }

    if (move.action === 'skip') {
      return { valid: true };
    }

    const pawn = this.state.pawns[player][move.pawnIndex];
    if (!pawn) {
      return { valid: false, reason: 'invalid_pawn' };
    }
    if (pawn.location === 'eliminated') {
      return { valid: false, reason: 'pawn_eliminated' };
    }
    if (pawn.location === 'home') {
      return { valid: false, reason: 'pawn_already_home' };
    }

    const dieValue = this.state.lastDiceResult.values[move.dieIndex];

    // Move validation
    if (move.action === 'move') {
      if (pawn.location === 'track') {
        const currentPos = pawn.trackPosition!;
        const distanceTraveled = this.getDistanceTraveled(player, currentPos);
        const totalAfterMove = distanceTraveled + dieValue;

        if (totalAfterMove > TRACK_LENGTH + HOME_COLUMN_LENGTH) {
          return { valid: false, reason: 'exceeds_home' };
        }

        if (this.isPathBlockedForMove(player, currentPos, dieValue, 'track')) {
          return { valid: false, reason: 'path_blocked' };
        }

        // Check destination for block
        if (totalAfterMove <= TRACK_LENGTH) {
          const destPos = (currentPos + dieValue) % TRACK_LENGTH;
          if (this.isBlockedByOpponent(player, destPos)) {
            return { valid: false, reason: 'blocked_by_block' };
          }
        }

        return { valid: true };
      }

      if (pawn.location === 'home_column') {
        const currentHomePos = pawn.homeColumnPosition!;
        const newHomePos = currentHomePos + dieValue;
        if (newHomePos > HOME_COLUMN_LENGTH) {
          return { valid: false, reason: 'exceeds_home' };
        }
        return { valid: true };
      }
    }

    return { valid: false, reason: 'invalid_pawn' };
  }

  executeMove(player: PlayerColor, move: ChauparMove): MoveResult {
    const validation = this.isValidMove(player, move);
    if (!validation.valid) {
      return {
        success: false,
        move,
        newPosition: this.state.pawns[player][move.pawnIndex] || { pawnIndex: -1, location: 'track' },
        reachedHome: false,
        error: validation.reason,
      };
    }

    if (move.action === 'skip') {
      this.state.usedDice[move.dieIndex] = true;
      return {
        success: true,
        move,
        newPosition: { pawnIndex: -1, location: 'track' },
        reachedHome: false,
      };
    }

    const pawn = this.state.pawns[player][move.pawnIndex];
    const fromPosition: PawnPosition = { ...pawn };
    let captured: PawnInfo | undefined;
    let reachedHome = false;

    if (move.action === 'move') {
      const dieValue = this.state.lastDiceResult!.values[move.dieIndex];

      if (pawn.location === 'track') {
        const currentPos = pawn.trackPosition!;
        const distanceTraveled = this.getDistanceTraveled(player, currentPos);
        const totalAfterMove = distanceTraveled + dieValue;

        if (totalAfterMove <= TRACK_LENGTH) {
          // Still on track
          const newPos = (currentPos + dieValue) % TRACK_LENGTH;
          captured = this.captureAtPosition(player, newPos);
          pawn.trackPosition = newPos;
        } else if (totalAfterMove <= TRACK_LENGTH + HOME_COLUMN_LENGTH) {
          // Entering home column
          const homeColPos = totalAfterMove - TRACK_LENGTH - 1;
          pawn.location = 'home_column';
          pawn.trackPosition = undefined;
          pawn.homeColumnPosition = homeColPos;
        }
      } else if (pawn.location === 'home_column') {
        const currentHomePos = pawn.homeColumnPosition!;
        const newHomePos = currentHomePos + dieValue;

        if (newHomePos === HOME_COLUMN_LENGTH) {
          // Reached home!
          pawn.location = 'home';
          pawn.homeColumnPosition = undefined;
          pawn.trackPosition = undefined;
          this.state.pawnsHome[player]++;
          reachedHome = true;
        } else {
          pawn.homeColumnPosition = newHomePos;
        }
      }
    }

    // Mark die as used
    this.state.usedDice[move.dieIndex] = true;
    this.state.lastMoveAt = Date.now();

    // Record move
    const moveRecord: MoveRecord = {
      moveNumber: this.state.moveHistory.length + 1,
      player,
      dieIndex: move.dieIndex,
      dieValue: move.dieValue,
      pawnIndex: move.pawnIndex,
      action: move.action,
      fromPosition,
      toPosition: { ...pawn },
      captured,
      reachedHome,
      timestamp: Date.now(),
    };
    this.state.moveHistory.push(moveRecord);

    // Check win condition
    this.checkWinCondition();

    return {
      success: true,
      move,
      newPosition: { ...pawn },
      captured,
      reachedHome,
    };
  }

  executeTurnMoves(player: PlayerColor, turnMoves: TurnMoveSet): TurnResult {
    const moveResults: MoveResult[] = [];

    for (const move of turnMoves.moves) {
      const result = this.executeMove(player, move);
      moveResults.push(result);
      if (!result.success) {
        return {
          success: false,
          moveResults,
          extraTurn: false,
          gameOver: this.state.gameStatus.type !== 'active',
          winningTeam: this.state.gameStatus.type === 'win' ? this.state.gameStatus.winningTeam : undefined,
        };
      }
    }

    // Handle bonus move
    if (turnMoves.bonusMove) {
      const bonusResult = this.executeMove(player, turnMoves.bonusMove);
      moveResults.push(bonusResult);
    }

    const extraTurn = this.state.lastDiceResult?.isTriple || false;
    const gameOver = this.state.gameStatus.type !== 'active';

    return {
      success: true,
      moveResults,
      extraTurn,
      gameOver,
      winningTeam: this.state.gameStatus.type === 'win' ? this.state.gameStatus.winningTeam : undefined,
    };
  }

  // ============================================================================
  // Turn Management
  // ============================================================================

  advanceTurn(): PlayerColor {
    if (this.state.lastDiceResult?.isTriple) {
      // Extra turn — don't advance
      this.state.lastDiceResult = null;
      this.state.usedDice = [false, false, false];
      this.state.awaitingMove = false;
      return this.state.currentTurn;
    }

    const turnOrder: PlayerColor[] = ['red', 'blue', 'green', 'yellow'];
    const currentIndex = turnOrder.indexOf(this.state.currentTurn);

    // Find next active player
    for (let i = 1; i <= 4; i++) {
      const nextIndex = (currentIndex + i) % 4;
      const nextPlayer = turnOrder[nextIndex];
      if (this.isPlayerActive(nextPlayer)) {
        this.state.currentTurn = nextPlayer;
        this.state.lastDiceResult = null;
        this.state.usedDice = [false, false, false];
        this.state.awaitingMove = false;
        return nextPlayer;
      }
    }

    return this.state.currentTurn;
  }

  hasExtraTurn(): boolean {
    return this.state.lastDiceResult?.isTriple || false;
  }

  getCurrentTurn(): PlayerColor {
    return this.state.currentTurn;
  }

  // ============================================================================
  // Board Queries
  // ============================================================================

  getSquareOccupants(position: number): PawnInfo[] {
    const occupants: PawnInfo[] = [];
    for (const color of PLAYER_COLORS) {
      for (const pawn of this.state.pawns[color]) {
        if (pawn.location === 'track' && pawn.trackPosition === position) {
          occupants.push({ player: color, pawnIndex: pawn.pawnIndex, position: { ...pawn } });
        }
      }
    }
    return occupants;
  }

  hasBlock(position: number): { blocked: boolean; team?: TeamColor } {
    const occupants = this.getSquareOccupants(position);
    if (occupants.length < 2) return { blocked: false };

    // Check if any two occupants are on the same team
    for (let i = 0; i < occupants.length; i++) {
      for (let j = i + 1; j < occupants.length; j++) {
        const teamI = this.getTeamForPlayer(occupants[i].player);
        const teamJ = this.getTeamForPlayer(occupants[j].player);
        if (teamI === teamJ) {
          return { blocked: true, team: teamI };
        }
      }
    }
    return { blocked: false };
  }

  isPathBlocked(player: PlayerColor, fromPos: number, toPos: number): boolean {
    const playerTeam = this.getTeamForPlayer(player);
    let pos = (fromPos + 1) % TRACK_LENGTH;

    while (pos !== (toPos + 1) % TRACK_LENGTH) {
      const block = this.hasBlock(pos);
      if (block.blocked && block.team !== playerTeam) {
        return true;
      }
      pos = (pos + 1) % TRACK_LENGTH;
      // Safety: prevent infinite loop
      if (pos === fromPos) break;
    }
    return false;
  }

  // ============================================================================
  // Team Queries
  // ============================================================================

  getTeamPawnsHome(team: TeamColor): number {
    const teamPlayers = TEAMS[team];
    return teamPlayers.reduce((sum, player) => sum + this.state.pawnsHome[player], 0);
  }

  getTeamPawnsEliminated(team: TeamColor): number {
    const teamPlayers = TEAMS[team];
    return teamPlayers.reduce((sum, player) => sum + this.state.eliminatedPawns[player], 0);
  }

  getTeamForPlayer(player: PlayerColor): TeamColor {
    if (TEAMS.teamA.includes(player)) return 'teamA';
    return 'teamB';
  }

  // ============================================================================
  // State Access
  // ============================================================================

  getGameState(): ChauparGameState {
    return { ...this.state };
  }

  getGameStatus(): ChauparGameStatus {
    return this.state.gameStatus;
  }

  isGameOver(): boolean {
    return this.state.gameStatus.type !== 'active';
  }

  getWinningTeam(): TeamColor | null {
    if (this.state.gameStatus.type === 'win') {
      return this.state.gameStatus.winningTeam;
    }
    if (this.state.gameStatus.type === 'resignation') {
      return this.state.gameStatus.winningTeam;
    }
    return null;
  }

  setGameState(state: ChauparGameState): void {
    this.state = { ...state };
  }

  // ============================================================================
  // Serialization
  // ============================================================================

  serialize(): string {
    return JSON.stringify(this.state);
  }

  deserialize(json: string): ChauparGameState | { error: string } {
    try {
      const parsed = JSON.parse(json);
      // Basic validation
      if (!parsed.pawns || !parsed.currentTurn || !parsed.gameStatus) {
        return { error: 'Invalid game state: missing required fields' };
      }
      if (!PLAYER_COLORS.includes(parsed.currentTurn)) {
        return { error: 'Invalid game state: invalid currentTurn' };
      }
      this.state = parsed;
      return parsed;
    } catch (e) {
      return { error: `Failed to parse game state: ${e}` };
    }
  }

  // ============================================================================
  // Private Helpers
  // ============================================================================

  private generateId(): string {
    return `chaupar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getDistanceTraveled(player: PlayerColor, currentTrackPos: number): number {
    const startPos = PLAYER_START_POSITIONS[player];
    if (currentTrackPos >= startPos) {
      return currentTrackPos - startPos;
    }
    return TRACK_LENGTH - startPos + currentTrackPos;
  }

  private isBlockedByOpponent(player: PlayerColor, position: number): boolean {
    const block = this.hasBlock(position);
    if (!block.blocked) return false;
    const playerTeam = this.getTeamForPlayer(player);
    return block.team !== playerTeam;
  }

  private isPathBlockedForMove(player: PlayerColor, fromPos: number, distance: number, _location: string): boolean {
    const playerTeam = this.getTeamForPlayer(player);

    for (let step = 1; step <= distance; step++) {
      const checkPos = (fromPos + step) % TRACK_LENGTH;
      const block = this.hasBlock(checkPos);
      if (block.blocked && block.team !== playerTeam) {
        return true;
      }
    }
    return false;
  }

  private captureAtPosition(player: PlayerColor, position: number): PawnInfo | undefined {
    const playerTeam = this.getTeamForPlayer(player);
    const occupants = this.getSquareOccupants(position);

    // Can only capture a single opponent pawn (not a block)
    const opponentPawns = occupants.filter(o => this.getTeamForPlayer(o.player) !== playerTeam);

    if (opponentPawns.length === 1) {
      // Check it's not part of a block
      const block = this.hasBlock(position);
      if (!block.blocked) {
        const target = opponentPawns[0];
        // Eliminate the pawn
        const pawn = this.state.pawns[target.player][target.pawnIndex];
        pawn.location = 'eliminated';
        pawn.trackPosition = undefined;
        pawn.homeColumnPosition = undefined;
        this.state.eliminatedPawns[target.player]++;
        return target;
      }
    }

    return undefined;
  }

  private isPlayerActive(player: PlayerColor): boolean {
    // A player is active if they have at least one pawn not eliminated and not home
    const pawns = this.state.pawns[player];
    return pawns.some(p => p.location !== 'eliminated' && p.location !== 'home');
  }

  private checkWinCondition(): void {
    // Check if a team has all 8 pawns home
    for (const team of ['teamA', 'teamB'] as TeamColor[]) {
      const teamPlayers = TEAMS[team];
      const totalHome = teamPlayers.reduce((sum, p) => sum + this.state.pawnsHome[p], 0);
      if (totalHome === PAWNS_PER_PLAYER * PLAYERS_PER_TEAM) {
        this.state.gameStatus = { type: 'win', winningTeam: team };
        return;
      }
    }

    // Check if all opponent pawns are eliminated
    for (const team of ['teamA', 'teamB'] as TeamColor[]) {
      const opponentTeam: TeamColor = team === 'teamA' ? 'teamB' : 'teamA';
      const opponentPlayers = TEAMS[opponentTeam];
      const allEliminated = opponentPlayers.every(p => {
        const pawns = this.state.pawns[p];
        return pawns.every(pawn => pawn.location === 'eliminated' || pawn.location === 'home');
      });
      // More specifically: all opponent pawns that aren't home are eliminated
      const opponentActive = opponentPlayers.some(p => {
        return this.state.pawns[p].some(pawn =>
          pawn.location !== 'eliminated' && pawn.location !== 'home'
        );
      });
      if (!opponentActive) {
        // Check that the winning team still has active pawns or all home
        this.state.gameStatus = { type: 'win', winningTeam: team };
        return;
      }
    }
  }
}
